import type { ReactNode } from "react";

import type { SignInFailure } from "../auth/authority.ts";

/**
 * The sign-in screen, from `02 Console Shell v3.dc.html`, screen "Sign in".
 *
 * A two-pane full-page layout: a dark brand pane that leaves the layout below 900, and a card on the
 * canvas carrying one of three steps. Every value here is read out of that file — `signGrid`,
 * `signBrand`, `signFacts` and the `sign` block — rather than approximated, and the copy is the
 * design's.
 *
 * The design has four steps and this has three, and since
 * `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md` the first of the three asks for nothing.
 *
 * **This screen is where CLAUDE.md section 1 is deliberately overridden, for this screen only.** The
 * design's first step collects a work email and its second confirms the organisation that address was
 * found in — "We found your organisation", "Continue with EAM Staff Login". The second was never
 * buildable: every IAM-02 read needs a scope and a tenant in its path, which an unauthenticated
 * browser has neither of, so discovery by email domain has no source at all (Q-16). The first was
 * built, and the operator has since decided against it — the email's only job was to reach the
 * provider as `login_hint`, the provider's own form asks for the same address, and two forms to
 * collect one password is one form too many. So the field is gone and this step carries only the
 * button that leaves.
 *
 * What is left of this screen is the three states that must not redirect: the confirmation after a
 * sign-out, the wait while the exchange finishes, and a failure. An unauthenticated arrival with
 * nothing to report never reaches it — `auth/arrival.tsx` sends that one to the provider.
 *
 * The design's third step is written for a popup window and this flow is a top-level redirect, so two
 * of its sentences are not true here. Recorded as Q-17, with the substitution named.
 */

export interface SignInProps {
  /**
   * Which of the design's steps to draw.
   *
   * `start` is the design's email step with its field removed: a title, a sentence and the button
   * that leaves for the provider. It is reached after a sign-out, after a provider the console could
   * not reach, and from the failure card's "Use a different account" — never on a plain arrival.
   */
  readonly step: "start" | "authenticating" | "failed";
  /** The design's green confirmation banner, after a sign-out that completed. */
  readonly afterSignOut: boolean;
  /** What this build calls the identity provider. See `VITE_OIDC_PROVIDER_NAME`. */
  readonly providerName: string;
  readonly failure?: SignInFailure;
  /** Leave for the provider. No argument: there is nothing on this screen left to carry. */
  readonly onContinue: () => void;
  /** The design's "Use a different account", which returns to the start step. */
  readonly onStartAgain: () => void;
}

export function SignIn({
  step,
  afterSignOut,
  providerName,
  failure,
  onContinue,
  onStartAgain,
}: SignInProps): ReactNode {
  const title =
    step === "failed"
      ? "Sign-in didn't complete"
      : step === "authenticating"
        ? "Finish signing in"
        : "Sign in to Operations";
  const subtitle =
    step === "failed"
      ? "Nothing was created and you are not signed in."
      : step === "authenticating"
        ? // The design says "A new window opened for your company sign-in.", which is a popup this
          // flow does not open. Q-17.
          `Completing sign-in with ${providerName}.`
        : // The design's sentence is "Enter your work email. We'll send you to the right sign-in
          // for your organisation." There is no email to enter now, so the half of it that is still
          // true is kept word for word and the half that is not is dropped. ADR 0022.
          "We'll send you to the right sign-in for your organisation.";

  return (
    <div className="signin">
      {/*
        The brand pane. `display:none` below 900 in the design, which the stylesheet does with a media
        query rather than a measured width — the design file measures its own frame because it is a
        frame inside a page, and this console is the page.
      */}
      <div className="signin__brand">
        <span className="signin__brand-row">
          {/*
            The product mark the rail already draws, rather than the design's four-square platform
            mark captioned "Platform · name TBD". Two reasons, and both are in the rules: the shell
            was already built with this mark and a console that named its own product two ways would
            be telling two stories, and "name TBD" is placeholder text, which section 1 says not to
            add. Recorded as Q-16.
          */}
          <span aria-hidden="true" className="signin__brand-mark">
            dashboard
          </span>
          <span className="signin__brand-name">Dispatch</span>
        </span>
        <span className="signin__brand-spacer" />
        <div className="signin__pitch">
          <span className="signin__headline">
            One sign-in for every console you're allowed into.
          </span>
          <span className="signin__lede">
            Your organisation decides how you sign in. After sign-in you land on the console and
            tenant you last used, with exactly the pages your role allows.
          </span>
        </div>
        <div className="signin__facts">
          <span className="signin__fact">
            <span aria-hidden="true" className="signin__fact-glyph">
              domain
            </span>
            You return to the tenant you last worked in, and it is always named on screen.
          </span>
          <span className="signin__fact">
            <span aria-hidden="true" className="signin__fact-glyph">
              devices
            </span>
            Sessions are per device and can be revoked from any other device (IAM-04).
          </span>
          <span className="signin__fact">
            <span aria-hidden="true" className="signin__fact-glyph">
              shield_person
            </span>
            Access comes from your role; the consoles only show what it allows.
          </span>
        </div>
      </div>

      <div className="signin__pane">
        <div className="signin__column">
          {afterSignOut ? (
            <div role="status" className="signin__signed-out">
              <span aria-hidden="true" className="signin__signed-out-glyph">
                check_circle
              </span>
              <span>Signed out on this device. Your other devices stay signed in.</span>
            </div>
          ) : null}

          <div className="signin__titles">
            <h1 className="signin__title">{title}</h1>
            <span className="signin__subtitle">{subtitle}</span>
          </div>

          <div className="signin__card">
            {step === "start" ? (
              /*
                The design's primary button, on its own.

                No form, because there is nothing to submit, and no wrapper: `.signin__card` is a
                grid with the same 14px gap `.signin__form` had, so the button sits exactly where the
                design's did. The label, the field and its error message went with the step they
                belonged to; their rules stay in the stylesheet, matching nothing here.
              */
              <button type="button" className="signin__primary" onClick={onContinue}>
                Continue
              </button>
            ) : null}

            {step === "authenticating" ? (
              <div role="status" className="signin__waiting">
                <span aria-hidden="true" className="signin__waiting-mark">
                  ?
                </span>
                <span>
                  <strong className="signin__waiting-strong">Waiting for {providerName}.</strong>{" "}
                  You are not signed in yet. If this did not finish, start again; nothing has been
                  created.
                </span>
              </div>
            ) : null}

            {step === "failed" && failure !== undefined ? (
              <>
                <div role="alert" className="signin__failure">
                  <span className="signin__category">
                    <span aria-hidden="true" className="signin__category-mark" />
                    authentication
                  </span>
                  <strong className="signin__failure-headline">{failure.headline}</strong>
                  <span className="signin__failure-detail">{failure.detail}</span>
                  {/*
                    The design's reference line carries an opaque correlation id. Nothing issues one
                    for a sign-in that never reached a service, so the line carries the provider's own
                    error name — which is the string somebody reporting this would be asked for
                    anyway. Q-17.
                  */}
                  <span className="signin__reference">Reference {failure.code}</span>
                </div>
                <button type="button" className="signin__secondary" onClick={onStartAgain}>
                  Use a different account
                </button>
              </>
            ) : null}
          </div>

          <span className="signin__foot">
            Workforce sign-in uses the organisation's OIDC provider (IAM-02). The session create
            payload (IAM-04 CreateSession) is not published; a platform-password path is not in the
            contracts and is not offered.
          </span>
        </div>
      </div>
    </div>
  );
}
